import '.ao{position:relative;}.ssr-plugin'
import '.at{flex-shrink:0;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.bc{height:100%;}.ssr-plugin'
import { styled, type Style } from 'inlines'
import { useEffect, useState } from 'react'
import { Image } from '../image/Image'
import { FileSelector } from '../input/text/FileSelector'
import { faUpload } from '@fortawesome/pro-regular-svg-icons'
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons'

export type AvatarSize =
  | 'extra-small'
  | 'small'
  | 'medium'
  | 'medium-large'
  | 'large'
  | 'extra-extra-small'
type Variant = 'pink' | 'yellow' | 'dimmed-red' | 'dark-red'
export type AvatarShape = 'square' | 'circle'

const sizeStyle = (shape: AvatarShape): Record<AvatarSize, Style> => ({
  large: {
    width: '130px',
    height: '130px',
    borderRadius: shape === 'circle' ? '80px' : '10px',
    fontSize: '42px',
  },
  'medium-large': {
    width: '100px',
    height: '100px',
    borderRadius: shape === 'circle' ? '60px' : '20px',
    fontSize: '32px',
  },
  medium: {
    width: '64px',
    height: '64px',
    borderRadius: shape === 'circle' ? '50px' : '10px',
    fontSize: '22px',
  },
  small: {
    width: '48px',
    height: '48px',
    borderRadius: shape === 'circle' ? '30px' : '10px',
    fontSize: '16px',
  },
  'extra-small': {
    width: '32px',
    height: '32px',
    borderRadius: shape === 'circle' ? '30px' : '10px',
    fontSize: '11px',
  },
  'extra-extra-small': {
    width: '25px',
    height: '25px',
    borderRadius: shape === 'circle' ? '30px' : '6px',
    fontSize: '11px',
  },
})

const resizeWidth: Record<AvatarSize, number> = {
  large: 130,
  'medium-large': 100,
  medium: 64,
  small: 48,
  'extra-small': 32,
  'extra-extra-small': 25,
}

const variantBackgroundStyle: Record<Variant, Style> = {
  pink: { backgroundColor: 'var(--brand-tertiary)' },
  yellow: { backgroundColor: 'var(--brand-accent)' },
  'dimmed-red': {
    backgroundColor: 'var(--palette-red-300)',
  },
  'dark-red': {
    backgroundColor: 'var(--palette-red-400)',
  },
}

export type AvatarProps = {
  size?: AvatarSize
  variant?: Variant
  image?: string | { src?: string }
  labelSource?: string | string[]
  shape?: AvatarShape
  editMode?: boolean
  onClick?: () => void
  onNewAvatarSeleted?: (file: File) => void
  style?: Style
}

export const Avatar = ({
  size = 'medium',
  variant = 'pink',
  image,
  labelSource = '',
  editMode = false,
  onClick,
  onNewAvatarSeleted,
  shape = 'circle',
  style,
}: AvatarProps) => {
  const [shouldRenderImage, setShouldRenderImage] = useState(false)
  const label = extractLabel(labelSource).toUpperCase()

  useEffect(() => {
    if (image) {
      setShouldRenderImage(true)
    }
  }, [image])

  const src = typeof image === 'object' ? image?.src : image

  if (editMode && onNewAvatarSeleted) {
    const isPreview = src?.startsWith('blob:')

    if (isPreview) {
      const sizes = sizeStyle(shape)[size]
      return (
        <styled.div className="ao at" style={{ ...sizes }}>
          <styled.div className="n bc"
            style={{
              background: `url(${src}) no-repeat center center / cover`,
              ...sizes,
            }}
          />
          <FileSelector
            icon={faArrowsRotate}
            iconStyle={{ fontSize: '0.75rem', color: 'var(--palette-white)' }}
            containerStyle={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              width: '1.5rem',
              height: '1.5rem',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onFileSelectionUpdated={(files) => onNewAvatarSeleted(files[0])}
          />
        </styled.div>
      )
    }

    return (
      <FileSelector
        icon={faUpload}
        iconStyle={{
          fontSize: '2rem',
        }}
        containerStyle={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          aspectRatio: '1/1',
          backgroundColor: 'var(--palette-white)',
          color: '#000',
          textAlign: 'center',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '100%',
          cursor: 'pointer',
          userSelect: 'none',
          overflow: 'hidden',
          background: `${!src ? variantBackgroundStyle[variant].backgroundColor : 'none'} url(${src}) no-repeat center center / cover`,
          filter: 'brightness(130%) contrast(70%)',
          ...sizeStyle(shape)[size],
        }}
        onFileSelectionUpdated={(files) => onNewAvatarSeleted(files[0])}
      />
    )
  }

  const Element = onClick ? styled.button : styled.div

  return (
    <Element
      onClick={onClick}
      style={{
        display: 'flex',
        flexShrink: 0,
        justifyContent: 'center',
        alignItems: 'center',
        aspectRatio: '1/1',
        backgroundColor: 'var(--palette-white)',
        color: '#000',
        textAlign: 'center',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '100%',
        userSelect: 'none',
        overflow: 'hidden',
        ...(onClick && {
          border: 'none',
          padding: 0,
          backgroundColor: 'transparent',
          cursor: 'pointer',
        }),
        ...sizeStyle(shape)[size],
        ...(!src ? variantBackgroundStyle[variant] : {}),
        ...style,
      }}
    >
      {shouldRenderImage ? (
        <Image
          src={src}
          alt={`${label}`}
          onError={() => setShouldRenderImage(false)}
          resizeWidth={resizeWidth[size]}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        label
      )}
    </Element>
  )
}

const extractLabel = (source: string | string[]): string => {
  if (!source || (Array.isArray(source) && source.length === 0)) {
    return ''
  }

  const stringSource = Array.isArray(source) ? source.join(' ') : String(source)

  if (
    stringSource.trim() === '' ||
    stringSource === 'null' ||
    stringSource === 'undefined'
  ) {
    return ''
  }

  if (stringSource.length < 3) {
    return stringSource.slice(0, 2)
  }

  const parts = stringSource.trim().split(' ')
  if (parts.length === 1) {
    return stringSource.slice(0, 2)
  }

  // If has 2 or more words return first letter of first and last words
  return parts[0][0] + parts[parts.length - 1][0]
}
